import React from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { useFormikContext } from "formik";
import { TextButton } from "@qgiv/core-donor";

const ParticipantSummaryChangeButton = ({
    baseUrl,
    changeParticipantDetailsLinkLabel,
    currentRegistration,
    isConfirmed,
    hasCurrentRegistrationBeenSubmitted,
    participantName,
    registration,
    registrationIndex,
    removeRegistration,
    setAppShowRegistrationRole,
    setAppShowRemainingForm,
    updateCurrentRegistration,
    updateCurrentRegistrationCategoryAndCurrentRegistration,
}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { pathname } = location;
    const isOnRegistrationPage = pathname.includes("form/registration");
    const { setValues } = useFormikContext();
    const handleChangeLinkClick = () => {
        // prevent action when viewing registration in summary
        // on the registration page
        if (currentRegistration === registrationIndex && isOnRegistrationPage) {
            return;
        }
        // Hiding then showing the form when changing registration is needed to clear the currency input
        dispatch(setAppShowRemainingForm(false));

        if (currentRegistration !== registrationIndex) {
            // If the user clicks change link from a Registration Page that has not been
            // saved then we should remove the unsaved registration
            if (!hasCurrentRegistrationBeenSubmitted) {
                dispatch(removeRegistration(currentRegistration));
            }
            const targetRegistrationCategory = registration?.Category?.[0];
            if (targetRegistrationCategory) {
                dispatch(
                    updateCurrentRegistrationCategoryAndCurrentRegistration({
                        newCurrentRegistration: registrationIndex,
                        newCurrentRegistrationCategory: [
                            targetRegistrationCategory,
                        ],
                    }),
                );
            } else {
                dispatch(updateCurrentRegistration(registrationIndex));
            }
        }

        // Delay needed to make sure the Show happens after the hide form
        setTimeout(() => {
            dispatch(setAppShowRemainingForm(true));
            dispatch(setAppShowRegistrationRole(true));
        }, 50);

        if (isOnRegistrationPage) {
            setValues(registration);
            return;
        }

        navigate(`${baseUrl}/form/registration`);
    };
    const ariaLabelVerb = isConfirmed
        ? changeParticipantDetailsLinkLabel
        : "Review";
    return (
        <TextButton
            ariaLabel={changeParticipantDetailsLinkLabel}
            handleClick={handleChangeLinkClick}
            aria-label={`${ariaLabelVerb} ${participantName}'s details'`}
        >
            <strong>
                {isConfirmed
                    ? changeParticipantDetailsLinkLabel
                    : "Review Details"}
            </strong>
        </TextButton>
    );
};

ParticipantSummaryChangeButton.propTypes = {
    baseUrl: PropTypes.string.isRequired,
    changeParticipantDetailsLinkLabel: PropTypes.string.isRequired,
    currentRegistration: PropTypes.number.isRequired,
    hasCurrentRegistrationBeenSubmitted: PropTypes.bool.isRequired,
    isConfirmed: PropTypes.bool.isRequired,
    participantName: PropTypes.string.isRequired,
    registration: PropTypes.shape({
        Category: PropTypes.arrayOf(PropTypes.string),
    }).isRequired,
    registrationIndex: PropTypes.number.isRequired,
    removeRegistration: PropTypes.func.isRequired,
    setAppShowRegistrationRole: PropTypes.func.isRequired,
    setAppShowRemainingForm: PropTypes.func.isRequired,
    updateCurrentRegistration: PropTypes.func.isRequired,
    updateCurrentRegistrationCategoryAndCurrentRegistration:
        PropTypes.func.isRequired,
};

export default ParticipantSummaryChangeButton;
